import { getBearing, getBearingText, getDistance, getDistanceText } from 'app/helpers';
import { useAppSelector } from 'app/store/store';
import { LineLayer, PickingInfo, ScatterplotLayer, TextLayer } from 'deck.gl/typed';
import { useEffect, useState } from 'react';

type PointType = [number, number];

export interface Line {
  sourcePosition: PointType;
  targetPosition: PointType;
  bearing?: number;
  length?: number;
  lenghtTotal?: number;
}

export const useRulerTools = () => {
  const rulerOn = useAppSelector((state) => state.mapStateSlice.tools.ruler);

  const [circleRadius, setCircleRadius] = useState<number>(0);
  const [mousePosition, setMousePosition] = useState<PointType | null>(null);
  const [startCoordinates, setStartCoordinates] = useState<PointType | null>(null);
  const [lines, setLines] = useState<Line[] | null>(null);
  const [isDraw, setIsDraw] = useState<boolean>(false);

  const stopDraw = (event: any) => {
    if (event.key === 'Enter' || event.key === 'Escape') {
      setIsDraw(false);
    }
    if (event.type === 'dblclick') {
      setIsDraw(false);
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', stopDraw);

    return () => {
      document.removeEventListener('keydown', stopDraw);
    };
  }, []);

  useEffect(() => {
    document.addEventListener('dblclick', stopDraw);
    return () => {
      document.removeEventListener('dblclick', stopDraw);
    };
  }, []);

  const handleMapClick = (info: PickingInfo) => {
    if (!rulerOn) return;

    setCircleRadius(0);

    if (!isDraw) {
      setIsDraw(true);
      setLines(null);
    } else {
      if (startCoordinates && info.coordinate) {
        const newLine = {
          sourcePosition: startCoordinates,
          targetPosition: info.coordinate as PointType,
        };
        const length = getDistance(newLine);
        const bearing = getBearing(newLine);
        const lenghtTotal = lines ? (lines[lines.length - 1]?.lenghtTotal || 0) + length : length;

        setLines([...(lines || []), { ...newLine, bearing, length, lenghtTotal }]);
      }
    }

    setStartCoordinates((info?.coordinate as PointType) || null);
  };

  const handleMapHover = (info: PickingInfo) => {
    if (info.viewport) {
      setMousePosition((info.coordinate as PointType) || null);
      if (startCoordinates) {
        const startPixel = info.viewport.project([startCoordinates[0], startCoordinates[1]]);
        if (info.pixel && startPixel) {
          const deltaX = info?.pixel[0] - startPixel[0];
          const deltaY = info?.pixel[1] - startPixel[1];
          const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
          setCircleRadius(distance);
        }
      }
    }
  };

  useEffect(() => {
    if (!rulerOn) {
      setLines(null);
      setCircleRadius(0);
      setStartCoordinates(null);
    }
  }, [rulerOn]);

  const layerScatterplot = new ScatterplotLayer({
    id: 'layer-scatterplot',
    data: isDraw
      ? [
          {
            coordinates: startCoordinates,
          },
        ]
      : [],

    stroked: true,
    filled: false,
    lineWidthMinPixels: 2,
    radiusUnits: 'pixels',
    getPosition: (d) => d.coordinates,
    getRadius: () => circleRadius,
    getLineColor: () => [255, 153, 0],
  });
  const layerPoints = new ScatterplotLayer({
    id: 'layer-points',
    data: lines ? [...lines] : undefined,

    filled: true,
    radiusUnits: 'pixels',
    getPosition: (d) => d.targetPosition,
    getRadius: () => 3,
    getFillColor: () => [255, 153, 0],
  });

  const layerLines = new LineLayer({
    id: 'layer-line',
    visible: true,
    data: lines
      ? [
          ...lines,
          mousePosition && startCoordinates && isDraw
            ? {
                sourcePosition: startCoordinates,
                targetPosition: mousePosition,
              }
            : [],
        ]
      : [
          mousePosition && startCoordinates && isDraw
            ? {
                sourcePosition: startCoordinates,
                targetPosition: mousePosition,
              }
            : [],
        ],

    getSourcePosition: (d) => d.sourcePosition,
    getTargetPosition: (d) => d.targetPosition,
    getWidth: 2,
    getColor: [255, 153, 0],
  });

  const layerCaption = new TextLayer({
    id: 'layer-ruler-caption',
    data: lines
      ? [
          ...lines,
          mousePosition && startCoordinates && isDraw
            ? {
                sourcePosition: startCoordinates,
                targetPosition: mousePosition,
                length: getDistance({ sourcePosition: startCoordinates, targetPosition: mousePosition }),
                bearing: getBearing({ sourcePosition: startCoordinates, targetPosition: mousePosition }),
              }
            : [],
        ]
      : [
          mousePosition && startCoordinates && isDraw
            ? {
                sourcePosition: startCoordinates,
                targetPosition: mousePosition,
                length: getDistance({ sourcePosition: startCoordinates, targetPosition: mousePosition }),
                bearing: getBearing({ sourcePosition: startCoordinates, targetPosition: mousePosition }),
              }
            : [],
        ],
    visible: Boolean(lines || (mousePosition && isDraw)),

    getPosition: (d) => {
      return d.targetPosition;
    },
    getPixelOffset: [-20, 30],
    characterSet: 'auto',
    getText: (d) => {
      if (d.lenghtTotal && d.length !== d.lenghtTotal) {
        return `${getBearingText(d.bearing)} / ${getDistanceText(d.length)}\n(${getDistanceText(d.lenghtTotal)})`;
      }
      if (d.bearing && d.length) {
        return `${getBearingText(d.bearing)}/${getDistanceText(d.length)}`;
      }
      return '';
    },
    background: true,
    backgroundPadding: [5, 5, 5, 5],
    getBackgroundColor: (d) => (d.bearing && d.length ? [255, 153, 0, 65] : [255, 153, 0, 0]),
    getSize: 12,
    getAngle: 0,
    getColor: [255, 255, 255],
    getTextAnchor: 'middle',
  });
  return {
    layer: [layerLines, layerPoints, layerScatterplot, layerCaption],
    handleClick: handleMapClick,
    handleHover: handleMapHover,
  };
};
